/**
 * Shared "Extracting key terms…" spinner atom.
 *
 * Surfaces a small spinner + caption while the backend is processing
 * an uploaded contract / transfer agreement. The atom is rendered:
 *  - inside `ContractDocumentListView` (desktop drawer) when
 *    `isExtracting` is true (sibling extraction PROCESSING or upload
 *    just kicked off — see `ContractDocumentListContainer`).
 *  - inside `ContractScreenView` (mobile full-page route) when the
 *    Transfer Agreement tab is active and the TA extraction is
 *    PROCESSING (SE-4766 mobile parity).
 *
 * Extracted from the inline JSX previously inlined in
 * `ContractDocumentListView` so both surfaces share the same testID
 * and copy. The testID is exported from the same module so callers
 * (and tests / e2e) reference one source of truth.
 * @module features/contracts/components/ContractExtractingIndicator/ContractExtractingIndicatorView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

/**
 * Shared testID for the extracting-key-terms spinner. Exported so
 * both unit tests and Playwright e2e specs reference the same string.
 * Real U+2026 ellipsis is preserved in the visible copy to match the
 * rest of the contract surface (`Loading contract documents…`,
 * `Extracting key terms…`).
 */
export const EXTRACTING_INDICATOR_TEST_ID = "contract-extracting-indicator";

/**
 * Pure presentation atom for the "Extracting key terms…" spinner row.
 * @returns The rendered spinner + caption pair
 */
const ContractExtractingIndicatorView = () => (
  <Box testID={EXTRACTING_INDICATOR_TEST_ID}>
    <Stack direction="horizontal" className="items-center gap-2">
      <Spinner size="small" />
      <Text variant="body" className="text-content-secondary">
        Extracting key terms…
      </Text>
    </Stack>
  </Box>
);

ContractExtractingIndicatorView.displayName = "ContractExtractingIndicatorView";

export default memo(ContractExtractingIndicatorView);
